/**
 * The Start block.
 *
 * It is drawn like a step but is not one: a plan enters at `steps[0]`, and what
 * decides which work orders get here — priorities, problem types, active window
 * — belongs to the Scenario. So it is never written to the plan, carries no
 * number, and cannot be removed or rewired. Its one output goes to the entry
 * step and is fixed.
 */

import { CaretRightOutlined } from '@ant-design/icons';
import { Handle, Position } from '@xyflow/react';
import React from 'react';
import { AOPScenario, AOP_OUT_HANDLE } from '../../../types/AOPTypes';
import { AOP_GROUP_TEXT_CLASS, AOP_NODE_BOUNDS_CLASS } from '../utils/AOPPlanUtils';
import {
	activeWindowLabel,
	describePriorityScope,
	describeProblemTypeScope,
} from '../utils/AOPScenarioUtils';

interface AOPStartNodeProps {
	data: { scenario: AOPScenario | null };
	selected?: boolean;
}

export const AOPStartNode: React.FC<AOPStartNodeProps> = ({ data, selected }) => {
	const scenario = data.scenario;
	const activeWindow = scenario ? scenario.active_window : null;

	return (
		<div className={`aop-node ${AOP_NODE_BOUNDS_CLASS}`} data-selected={selected ? '' : undefined}>
			<div className="flex items-center gap-xs border-0 border-b border-solid border-grey-3 px-s py-xs">
				<span
					className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded border border-solid border-grey-5 bg-grey-1 ${AOP_GROUP_TEXT_CLASS.lifecycle}`}
				>
					<CaretRightOutlined />
				</span>
				{/*
				  No number. Start is not in `plan.steps`: it cannot be edited, removed,
				  reordered or pointed at by an exit, and the branch pickers build their
				  options from the steps, so it can never be a target. Numbering it
				  would put it in a sequence it is not part of and push every real step
				  one out of step with the plan the service stores.
				*/}
				<span className="bodymedium min-w-0 flex-1 text-character-primary">Start</span>
			</div>

			<div className="flex flex-col px-s py-xs">
				{scenario ? (
					<>
						<span className="bodyregular text-character-primary">
							{describePriorityScope(scenario.priority_ids)}
						</span>
						<span className="bodyregular text-character-primary">
							{describeProblemTypeScope(scenario.problem_type_ids)}
						</span>
						<span className="bodyregular text-character-primary">
							{activeWindowLabel(activeWindow)}
						</span>
					</>
				) : (
					<span className="bodyregular text-character-secondary">
						No scenario routes work orders here yet.
					</span>
				)}
			</div>

			{/*
			  Entry is `steps[0]`, which reordering the plan would change — it is not
			  an exit, so there is nothing to drag.
			*/}
			<Handle
				type="source"
				position={Position.Right}
				id={AOP_OUT_HANDLE}
				isConnectable={false}
				className="aop-handle aop-handle-fixed"
			/>
		</div>
	);
};
